import {useMemo} from 'react';

import dayjs from 'dayjs';
import {Observer} from 'mobx-react';
import styled, {css} from 'styled-components';

import type {
  CRUProgramScheduleConferenceStore,
  CRUProgramScheduleTaskStore,
} from '@yourcoach/shared/modules/program/cru/schedule';

import {useI18n} from '@yourcoach/i18n';
import {Form} from '@yourcoach/shared/components/Form';
import {RecurringEventDisclaimer} from '@yourcoach/shared/components/RecurringEventDisclaimer';
import {REPEATS} from '@yourcoach/shared/modules/program/cru/schedule';
import {getColor} from '@yourcoach/shared/styles/utils';
import {Button} from '@yourcoach/shared/uikit/Button';
import {Clickable} from '@yourcoach/shared/uikit/Clickable';
import {Tooltip} from '@yourcoach/shared/uikit/Tooltip';
import {View} from '@yourcoach/shared/uikit/View';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import List from '@src/components/List';
import Modal from '@src/components/ModalNew';

export const useCRUScheduleEvent = (
  store: InstanceType<
    | typeof CRUProgramScheduleConferenceStore
    | typeof CRUProgramScheduleTaskStore
  >,
  onSaveButtonClick: () => void,
  mode: 'conference' | 'task',
) => {
  const {t} = useI18n();

  const titleSection = useMemo(
    () => (
      <Form.Section isFirst>
        <Form.Label>
          {t('shared:CRUProgramScheduleEvent.title_label')}
        </Form.Label>
        <Observer>
          {() => (
            <Form.TextInput
              error={store.fields.title.error || ''}
              onChange={store.fields.title.onChange}
              placeholder={t(
                'shared:CRUProgramScheduleEvent.title_text_input_placeholder',
              )}
              ref={store.fields.title.ref}
              testID={TestID.schedule_event.input.title}
              value={store.fields.title.value}
            />
          )}
        </Observer>
      </Form.Section>
    ),
    [store, t],
  );

  const repeatSection = useMemo(
    () => (
      <Form.Section isFirst={mode === 'task'}>
        <Observer>
          {() => (
            <Form.LabelContainer>
              <Form.Label>
                {t('shared:CRUProgramScheduleEvent.repeat_label')}
              </Form.Label>
              {store.shouldShowRecurringDisclaimer ? (
                <Tooltip
                  content={<RecurringEventDisclaimer eventType={mode} />}
                  place="bottom"
                  webProps={{
                    display: 'flex',
                  }}
                  width={400}
                >
                  <RecurringDisclaimerLabel>
                    {store.recurringDisclaimerTitle}
                  </RecurringDisclaimerLabel>
                </Tooltip>
              ) : null}
            </Form.LabelContainer>
          )}
        </Observer>
        <Observer>
          {() => (
            <Form.OptionsWrapper>
              <Form.OptionsContainer>
                {REPEATS.map((repeat, i, itself) => {
                  const isSelected = repeat.id === store.repeat.id;
                  const isAvailable = store.isRepeatAvailable(repeat);

                  return (
                    <Clickable
                      key={repeat.id}
                      onClick={
                        isAvailable ? () => store.setRepeat(repeat) : undefined
                      }
                      style={
                        !isAvailable
                          ? {
                              cursor: 'not-allowed',
                            }
                          : undefined
                      }
                      testID={TestID.schedule_event.button[repeat.id]}
                    >
                      <SForm.Option
                        isDisabled={!isAvailable}
                        isLast={i === itself.length - 1}
                        isSelected={isSelected}
                      >
                        <SForm.OptionText isSelected={isSelected}>
                          {repeat.toString()}
                        </SForm.OptionText>
                      </SForm.Option>
                    </Clickable>
                  );
                })}
              </Form.OptionsContainer>
            </Form.OptionsWrapper>
          )}
        </Observer>
      </Form.Section>
    ),
    [mode, store, t],
  );

  const daySection = useMemo(
    () => (
      <Observer>
        {() =>
          store.dayIsAvailable ? (
            <Form.Section>
              <Form.Label>
                {store.isRecurring
                  ? t('shared:CRUProgramScheduleEvent.start_day_label')
                  : t('shared:CRUProgramScheduleEvent.day_label')}
              </Form.Label>
              <Form.TextInput
                error={store.fields.day.error || ''}
                onChange={store.onDayChange}
                placeholder={t(
                  'shared:CRUProgramScheduleEvent.day_text_input_placeholder',
                  {
                    max_day: store.programDuration,
                    min_day: 1,
                  },
                )}
                ref={store.fields.day.ref}
                testID={TestID.schedule_event.input.start_day}
                value={store.fields.day.value}
              />
            </Form.Section>
          ) : null
        }
      </Observer>
    ),
    [store, t],
  );

  const timeSection = useMemo(
    () => (
      <Form.Section>
        <Form.Label>
          {t('shared:CRUProgramScheduleEvent.time_label')}
        </Form.Label>
        <Observer>
          {() => (
            <Form.TextInput
              defaultValue={dayjs(store.date).format('HH:mm')}
              onChange={store.onTimeChange}
              testID={TestID.schedule_event.input.time}
              webProps={{
                type: 'time',
              }}
            />
          )}
        </Observer>
      </Form.Section>
    ),
    [store, t],
  );

  const descriptionSection = useMemo(
    () => (
      <Form.Section>
        <Form.Label>
          {t('shared:CRUProgramScheduleEvent.description_label')}
          <Form.Label optional>
            {` (${t('shared:CRUProgramScheduleEvent.optional_label')})`}
          </Form.Label>
        </Form.Label>
        <Observer>
          {() => (
            <Form.TextArea
              error={store.fields.description.error || ''}
              onChange={store.fields.description.onChange}
              placeholder={t(
                'shared:CRUProgramScheduleEvent.description_text_input_placeholder',
              )}
              ref={store.fields.description.ref}
              value={store.fields.description.value}
            />
          )}
        </Observer>
      </Form.Section>
    ),
    [store, t],
  );

  const footer = useMemo(
    () => (
      <Footer>
        <Observer>
          {() => (
            <Button
              onClick={onSaveButtonClick}
              scheme="secondary"
              testID={TestID.schedule_event.button.save}
            >
              {store.saveButtonLabel}
            </Button>
          )}
        </Observer>
      </Footer>
    ),
    [store, onSaveButtonClick],
  );

  return {
    daySection,
    descriptionSection,
    footer,
    repeatSection,
    timeSection,
    titleSection,
  };
};

const Footer = styled(View)`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
`;

const SModalTask = styled(Modal)`
  .body.body {
    width: 65vw;
    padding: 0;
    height: 60vh !important;
    min-height: 200px !important;
  }
`;

const SModalConference = styled(SModalTask)`
  .body.body {
    width: 65vw;
    padding: 0;
    height: 61vh !important;
    min-height: 200px !important;
  }
`;

export {SModalConference as ModalConference, SModalTask as ModalTask};

export const Container = styled(View)`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  padding: 20px;
`;

const SList = styled(List)`
  height: 100%;
`;

export {SList as List};

const SForm = {
  Option: styled(Form.Option)`
    border-color: ${getColor('background2')};

    ${p =>
      p.isSelected &&
      css`
        border-color: ${getColor('global2')};
        background-color: ${getColor('background5')};
      `}
  `,
  OptionText: styled(Form.OptionText)`
    white-space: nowrap;

    ${p =>
      p.isSelected &&
      css`
        color: ${getColor('global2')};
      `}
  `,
};

const RecurringDisclaimerLabel = styled(Form.Label)`
  color: ${getColor('text3')};
  text-decoration: underline solid ${getColor('text3')};
  margin-left: 10px;
  text-align: right;
`;
